import { useEffect, useMemo, useState } from 'react'
import { adminApiService } from '../../services/adminApiService'
import { notificationsApiService, NotificationType } from '../../services/notificationsApiService'
import { ApiError } from '../../services/BaseApiService'
import type { AdminTeam } from '../../types'
import { colors, fontSize, radius, spacing, tracking } from '../../styles/theme'
import { Button, Input, Select, SectionCard } from '../../components/ui'
import { RichTextEditor } from '../../components/RichTextEditor'
import AdminTabs from './AdminTabs'

const TYPE_OPTIONS = [
  { value: NotificationType.STANDARD, label: 'Standard — bell only' },
  { value: NotificationType.WARNING, label: 'Warning — alarming modal' },
  { value: NotificationType.ANNOUNCEMENT, label: 'Announcement — modal' },
]
const GROUP_OPTIONS = [
  { value: 'all', label: 'All users in the team' },
  { value: 'team_admins', label: 'Team admins only' },
]

// A recipient target: a predefined group within a team. The selection lives in a modal because it
// will grow (individual users, more group kinds) over time.
interface Target { teamId: string; teamName: string; group: string; groupLabel: string }

export default function AdminNotificationsPage() {
  const [teams, setTeams] = useState<AdminTeam[]>([])
  const [type, setType] = useState<number>(NotificationType.STANDARD)
  const [title, setTitle] = useState('')
  const [body, setBody] = useState('')
  const [sendEmail, setSendEmail] = useState(false)
  const [target, setTarget] = useState<Target | null>(null)
  const [picking, setPicking] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [result, setResult] = useState('')

  useEffect(() => {
    adminApiService.getTeams(1, 200).then(r => setTeams(r.items)).catch(() => {})
  }, [])

  const canSend = !!title.trim() && !!target && !busy

  async function send() {
    if (!target) return
    setBusy(true); setError(''); setResult('')
    try {
      const r = await notificationsApiService.send({
        type, title: title.trim(), body: body.trim() || null,
        teamId: target.teamId, group: target.group, userIds: null, sendEmail,
      })
      setResult(`Sent to ${r.sent} recipient${r.sent === 1 ? '' : 's'}.`)
      setTitle(''); setBody('')
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Failed to send')
    } finally { setBusy(false) }
  }

  return (
    <div>
      <AdminTabs />
      <h1 style={{ fontSize: fontSize['2xl'], fontWeight: 600, color: colors.text, margin: 0, letterSpacing: tracking.tight, marginBottom: spacing.xl }}>
        Send notification
      </h1>

      <div style={{ maxWidth: 560 }}>
        <SectionCard title="Compose">
          <div style={{ marginBottom: spacing.md }}>
            <Select label="Type" value={type} onChange={v => setType(Number(v))} options={TYPE_OPTIONS} />
          </div>
          <div style={{ marginBottom: spacing.md }}>
            <Input label="Title" value={title} maxLength={200} onChange={e => setTitle(e.target.value)} placeholder="Scheduled maintenance" />
          </div>
          <div style={{ marginBottom: spacing.md }}>
            <div style={{ fontSize: fontSize.sm, fontWeight: 500, marginBottom: spacing.xs }}>Message</div>
            <RichTextEditor value={body} onChange={setBody} />
            <div style={{ fontSize: fontSize.xs, color: colors.textSecondary, marginTop: spacing.xs }}>
              Supports formatting, fonts, colour, links, images and video.
            </div>
          </div>

          <div style={{ marginBottom: spacing.md }}>
            <div style={{ fontSize: fontSize.sm, fontWeight: 500, marginBottom: spacing.xs }}>Recipients</div>
            {target
              ? <div style={{ fontSize: fontSize.sm, color: colors.text }}>{target.groupLabel} · <strong>{target.teamName}</strong>{' '}
                  <button onClick={() => setPicking(true)} style={{ background: 'none', border: 'none', color: colors.primary, cursor: 'pointer', fontSize: fontSize.sm }}>change</button></div>
              : <Button size="sm" variant="secondary" onClick={() => setPicking(true)}>Select recipients…</Button>}
          </div>

          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: fontSize.sm, marginBottom: spacing.lg, cursor: 'pointer' }}>
            <input type="checkbox" checked={sendEmail} onChange={e => setSendEmail(e.target.checked)} />
            Also send as email
          </label>

          {error && <div style={{ color: colors.error, fontSize: fontSize.sm, marginBottom: spacing.md }}>{error}</div>}
          {result && <div style={{ color: colors.success, fontSize: fontSize.sm, marginBottom: spacing.md }}>{result}</div>}
          <Button disabled={!canSend} loading={busy} onClick={send}>Send notification</Button>
        </SectionCard>
      </div>

      {picking && (
        <RecipientModal teams={teams} onClose={() => setPicking(false)} onPick={t => { setTarget(t); setPicking(false) }} />
      )}
    </div>
  )
}

function RecipientModal({ teams, onClose, onPick }: { teams: AdminTeam[]; onClose: () => void; onPick: (t: Target) => void }) {
  const [teamId, setTeamId] = useState('')
  const [group, setGroup] = useState('all')
  const teamName = useMemo(() => teams.find(t => t.teamId === teamId)?.name ?? '', [teams, teamId])
  const groupLabel = GROUP_OPTIONS.find(g => g.value === group)?.label ?? group

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 }}>
      <div style={{ background: colors.surface, borderRadius: radius.md, padding: spacing.lg, width: 420, maxWidth: '100%' }}>
        <div style={{ fontWeight: 600, color: colors.text, marginBottom: spacing.md }}>Select recipients</div>
        <div style={{ marginBottom: spacing.md }}>
          <Select label="Team" placeholder="Select a team…" isSearchable value={teamId} onChange={v => setTeamId(String(v))}
            options={teams.map(t => ({ value: t.teamId, label: t.name }))} />
        </div>
        <div style={{ marginBottom: spacing.lg }}>
          <Select label="Group" value={group} onChange={v => setGroup(String(v))} options={GROUP_OPTIONS} />
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: spacing.sm }}>
          <Button size="sm" variant="ghost" onClick={onClose}>Cancel</Button>
          <Button size="sm" disabled={!teamId} onClick={() => onPick({ teamId, teamName, group, groupLabel })}>Use these recipients</Button>
        </div>
      </div>
    </div>
  )
}
